import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Link,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { useParams, Link as RouterLink } from "react-router-dom";
import currency from "currency.js";
import moment from "moment";
import bkstApi from "../../api";
import BDatePicker from "../../common/input/BDatePicker";
import SearchIcon from "@mui/icons-material/Search";
import { BarChart } from "@mui/x-charts/BarChart";
import { DataGrid } from "@mui/x-data-grid";
import ItemFilter from "../../planner/component/ItemFilter";
import { UserContext } from "../../context/UserContext";
import { getStoreFilterStreets, getStoreLocationIdForFilter } from "../../common/util";

export default function PerformanceReport() {
  const { bakerId } = useParams();
  const { user } = useContext(UserContext);

  const [fromDt, setFromDt] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [toDt, setToDt] = useState(moment().format("YYYY-MM-DD"));
  const [report, setReport] = useState();
  const [loading, setLoading] = useState(false);
  const [storeFilter, setStoreFilter] = useState({});
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  useEffect(() => {
    getData(fromDt, toDt);
  }, []);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > 31) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const getData = (from, to, storeLocationId = "") => {
    setLoading(true);
    const locQ = storeLocationId ? `&storeLocationId=${encodeURIComponent(storeLocationId)}` : "";
    bkstApi(`/my/report/performance?from=${from}&to=${to}${locQ}`).then((res) => {
      setReport(res.data);
      setLoading(false);
    });
  };

  const runDateSearch = (e) => {
    e.preventDefault();
    getData(fromDt, toDt, getStoreLocationIdForFilter(user, storeFilter?.store?.value));
  };

  const onStoreFilterChange = (o) => {
    const next = { ...storeFilter, ...o };
    const prevId = getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    const nextId = getStoreLocationIdForFilter(user, next?.store?.value);
    setStoreFilter(next);
    if (fromDt && toDt && prevId !== nextId) {
      getData(fromDt, toDt, nextId);
    }
  };

  const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

  return (
      <Box>
        <Container maxWidth="md">
          <Box mt={4}>
            <Typography variant="h1" gutterBottom>
              Performance Report
            </Typography>
            <Box my={3}>
              <form onSubmit={runDateSearch}>
                <Box sx={{ display: "flex", gap: "10px" }}>
                  <BDatePicker
                    value={fromDt}
                    variant="filled"
                    label="From Date"
                    onChange={(d) => setFromDt(d)}
                    required
                    autoOk
                    disableFuture
                    hideIcon={true}
                    minDate={moment().subtract(1, "year").toDate()}
                  />
                  <BDatePicker
                    value={toDt}
                    variant="filled"
                    label="To Date"
                    onChange={(d) => setToDt(d)}
                    required
                    autoOk
                    disableFuture
                    hideIcon={true}
                    minDate={fromDt ? moment(fromDt).toDate() : undefined}
                    maxDate={
                      fromDt
                        ? moment.min(moment(), moment(fromDt).add(31, "days")).toDate()
                        : moment().toDate()
                    }
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ height: "56px" }}
                  >
                    <SearchIcon />
                  </Button>
                </Box>
              </form>
            </Box>
            {showStoreFilter && (
              <Box mb={2}>
                <ItemFilter
                  config={[{ name: "store", options: storeStreets }]}
                  onChange={onStoreFilterChange}
                />
              </Box>
            )}
          </Box>
          {loading ? (
            <LinearProgress />
          ) : report && report.orders.count > 0 ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <Box p={2} pt={4} sx={{ backgroundColor: "#ECECEC", flexGrow: 1 }}>
                    <Box textAlign={"center"}>
                      <Typography variant="h3">
                        {currency(report.orders.sales, { precision: 0 }).format()}
                      </Typography>
                      <Typography variant="overline" color="textSecondary" display="block">
                        TOTAL SALES
                      </Typography>
                      <Typography variant="overline">{report.orders.count} ORDERS</Typography>
                    </Box>
                  </Box>{" "}
                  {report.orders.segment.map((segment, idx) => {
                    const chartData = Object.entries(segment.data)
                      .sort(([sdk1], [sdk2]) => collator.compare(sdk1, sdk2))
                      .map(([key, value]) => ({
                        label: key,
                        sales: value.sales,
                        count: value.count,
                      }));
                    return (
                      <Box my={4} key={idx}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: "capitalize" }}>
                          Sales by {segment.title}
                        </Typography>
                        <Paper elevation={0}>
                          {chartData.length > 1 && (
                            <BarChart
                              dataset={chartData}
                              xAxis={[
                                { scaleType: "band", dataKey: "label", categoryGapRatio: 0.7 },
                              ]}
                              series={[
                                {
                                  dataKey: "sales",
                                  label: "Sales",
                                  color: "#78374a",
                                  valueFormatter: (value) =>
                                    currency(value, { precision: 0 }).format(),
                                },
                              ]}
                              height={200}
                              margin={{ left: 40 }}
                              slotProps={{ legend: { hidden: true } }}
                            />
                          )}
                          <DataGrid
                            rows={Object.entries(segment.data)
                              .sort(([sdk1], [sdk2]) => collator.compare(sdk1, sdk2))
                              .map(([key, value], index) => ({
                                id: index,
                                label: key,
                                sales: value.sales,
                                count: value.count,
                              }))}
                            columns={[
                              { field: "label", headerName: segment.title, flex: 1 },
                              { field: "count", headerName: "Orders", width: 80, type: "number" },
                              {
                                field: "sales",
                                headerName: "Sales",
                                width: 100,
                                valueFormatter: ({ value }) =>
                                  currency(value, { precision: 0 }).format(),
                              },
                            ]}
                            autoHeight
                            hideFooter
                            disableRowSelectionOnClick
                            disableColumnMenu
                            getRowClassName={(params) =>
                              params.row.sales === 0 || params.row.count === 0
                                ? "zero-value-row"
                                : ""
                            }
                            sx={{
                              border: "none",
                              "& .zero-value-row": {
                                backgroundColor: "#fdf2f2",
                              },
                            }}
                          />
                        </Paper>
                      </Box>
                    );
                  })}
                </Grid>

                <Grid item sm={6} xs={12}>
                  <Box p={2} pt={4} sx={{ backgroundColor: "#ECECEC", flexGrow: 1 }}>
                    <Box textAlign={"center"}>
                      <Typography variant="h3">{report.quotes.count}</Typography>
                      <Typography variant="overline" color="textSecondary" display="block">
                        LEADS
                      </Typography>
                      <Typography variant="overline">
                        {report.quotes.conversion.orders} ORDERS
                      </Typography>
                    </Box>
                  </Box>
                  {report.quotes.segment.map((segment, idx) => {
                    const chartData = Object.entries(segment.data)
                      .sort(([sdk1], [sdk2]) => collator.compare(sdk1, sdk2))
                      .map(([key, value]) => ({
                        label: key,
                        leads: value.count,
                        orders: value.orders,
                      }));
                    return (
                      <Box my={4}>
                        <Typography variant="h4" gutterBottom sx={{ textTransform: "capitalize" }}>
                          Leads by {segment.title}
                        </Typography>
                        <Paper elevation={0}>
                          {chartData.length > 1 && (
                            <BarChart
                              dataset={chartData}
                              xAxis={[
                                { scaleType: "band", dataKey: "label", categoryGapRatio: 0.7 },
                              ]}
                              series={[
                                {
                                  dataKey: "leads",
                                  label: "Leads",
                                  color: "#78374a",
                                },
                              ]}
                              height={200}
                              margin={{ left: 40 }}
                              slotProps={{ legend: { hidden: true } }}
                            />
                          )}
                          <DataGrid
                            rows={Object.entries(segment.data)
                              .sort(([sdk1], [sdk2]) => collator.compare(sdk1, sdk2))
                              .map(([key, value], index) => ({
                                id: index,
                                label: key,
                                leads: value.count,
                                orders: value.orders,
                              }))}
                            columns={[
                              { field: "label", headerName: segment.title, flex: 1 },
                              { field: "leads", headerName: "Leads", width: 80, type: "number" },
                              { field: "orders", headerName: "Orders", width: 80, type: "number" },
                            ]}
                            autoHeight
                            hideFooter
                            disableRowSelectionOnClick
                            disableColumnMenu
                            getRowClassName={(params) =>
                              params.row.leads === 0 || params.row.orders === 0
                                ? "zero-value-row"
                                : ""
                            }
                            sx={{
                              border: "none",
                              "& .zero-value-row": {
                                backgroundColor: "#fdf2f2",
                              },
                            }}
                          />
                        </Paper>
                      </Box>
                    );
                  })}
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box textAlign={"center"} py={4}>
              <Typography variant="h4">No data found</Typography>
            </Box>
          )}
        </Container>
      </Box>
    );
}
